import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Loader2,
  Mail,
  CheckCircle,
  Package,
  Bell,
  Shield,
  Gift,
  Headphones
} from 'lucide-react';

const emailSchema = z.object({
  email: z
    .string()
    .email('Please enter a valid email address')
    .min(1, 'Email is required'),
});

type EmailFormData = z.infer<typeof emailSchema>;

interface EmailCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
  title?: string;
  description?: string;
  benefits?: string[];
  showBenefits?: boolean;
  useGradientStyling?: boolean;
  skipOption?: {
    text: string;
    subtext: string;
  };
}

export const EmailCollectionModal: React.FC<EmailCollectionModalProps> = ({
  isOpen,
  onClose,
  onComplete,
  title = "Complete Your Profile",
  description = "Add your email to unlock these benefits:",
  benefits = [
    "Order confirmations and receipts",
    "Important shipping notifications",
    "Exclusive deals and promotions",
    "Customer support communication",
    "Account security updates"
  ],
  showBenefits = true,
  useGradientStyling = true,
  skipOption = {
    text: "I'll add it later",
    subtext: "You can always add your email from your profile settings"
  }
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const form = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: '',
    },
  });

  const benefitIcons = [Package, Bell, Gift, Headphones, Shield];

  const handleSubmit = async (data: EmailFormData) => {
    if (!user) {
      toast({
        title: 'Error',
        description: 'No user found. Please sign in again.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      // Update the user's email in auth.users
      const { error: updateError } = await supabase.auth.updateUser({
        email: data.email,
      });

      if (updateError) {
        throw updateError;
      }

      // Update profile table
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          email: data.email,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (profileError) {
        console.error('Profile update error:', profileError);
        // Don't throw here - email was updated in auth, profile sync can happen later
      }

      toast({
        title: 'Email Added Successfully! 📧',
        description: 'You\'ll now receive order confirmations and important updates.',
      });

      onComplete();

    } catch (error: any) {
      console.error('Email update error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update email. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    toast({
      title: 'Email Skipped',
      description: 'You can add your email later from your profile settings.',
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={`w-[95vw] max-w-md mx-3 sm:mx-auto max-h-[85vh] overflow-y-auto ${
          useGradientStyling
            ? 'bg-gradient-to-br from-white via-teal-50/30 to-amber-50/20 border-teal-200/50'
            : ''
        }`}
      >
        <DialogHeader className="text-center space-y-2 sm:space-y-3">
          <div className="mx-auto w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center shadow-lg">
            <Mail className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
          </div>

          <DialogTitle className="text-lg sm:text-xl font-semibold bg-gradient-to-r from-teal-600 to-teal-700 bg-clip-text text-transparent">
            {title}
          </DialogTitle>

          <DialogDescription className="text-sm sm:text-base text-gray-600 px-2">
            {description}
          </DialogDescription>
        </DialogHeader>

        {showBenefits && (
          <div className="space-y-2 sm:space-y-3 my-3 sm:my-4">
            {benefits.map((benefit, index) => {
              const IconComponent = benefitIcons[index] || CheckCircle;
              return (
                <div key={index} className="flex items-center space-x-2 sm:space-x-3 text-xs sm:text-sm">
                  <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-teal-100 to-teal-200 rounded-lg flex items-center justify-center">
                    <IconComponent className="w-4 h-4 text-teal-600" />
                  </div>
                  <span className="text-gray-700 font-medium">{benefit}</span>
                </div>
              );
            })}
          </div>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-3 sm:space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs sm:text-sm font-medium text-gray-700">
                    Email Address
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Mail className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        {...field}
                        type="email"
                        placeholder="Enter your email address"
                        className="pl-10 h-11 border-gray-200 focus:border-teal-500 focus:ring-teal-500"
                        disabled={loading}
                        autoFocus
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-3 pt-2">
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-10 sm:h-11 text-sm sm:text-base bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white font-medium transition-all duration-200 shadow-md hover:shadow-lg"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding Email...
                  </>
                ) : (
                  <>
                    <Mail className="mr-2 h-4 w-4" />
                    Add Email Address
                  </>
                )}
              </Button>

              {skipOption && (
                <div className="text-center space-y-1">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleSkip}
                    disabled={loading}
                    className="text-gray-500 hover:text-gray-700 text-sm font-medium"
                  >
                    {skipOption.text}
                  </Button>
                  <p className="text-xs text-gray-400 px-4">
                    {skipOption.subtext}
                  </p>
                </div>
              )}
            </div>
          </form>
        </Form>

        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-center text-xs text-gray-500 space-x-2">
            <Shield className="w-3 h-3" />
            <span>Your email is secure and will never be shared</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};